interface ProgressBarProps {
  percentage: number;
  size?: "sm" | "md" | "lg";
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "error"
    | "white";
  showLabel?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: "h-1.5",
  md: "h-2",
  lg: "h-3",
} as const;

const colorClasses = {
  default: {
    fill: "bg-[var(--progress)]",
    track: "bg-[var(--progress-track)]",
    label: "text-muted-foreground",
  },
  primary: {
    fill: "bg-[var(--primary)]",
    track: "bg-[var(--progress-track)]",
    label: "text-muted-foreground",
  },
  secondary: {
    fill: "bg-[var(--secondary)]",
    track: "bg-[var(--progress-track)]",
    label: "text-muted-foreground",
  },
  success: {
    fill: "bg-[var(--success)]",
    track: "bg-[var(--success-light)]",
    label: "text-muted-foreground",
  },
  warning: {
    fill: "bg-[var(--warning)]",
    track: "bg-[var(--warning-light)]",
    label: "text-muted-foreground",
  },
  error: {
    fill: "bg-[var(--error)]",
    track: "bg-[var(--error-light)]",
    label: "text-muted-foreground",
  },
  white: { fill: "bg-white", track: "bg-white/20", label: "text-white/80" },
} as const;

export const ProgressBar = ({
  percentage,
  size = "md",
  color = "default",
  showLabel = true,
  className,
}: ProgressBarProps) => {
  const h = sizeClasses[size];
  const { fill, track, label } = colorClasses[color];

  const bar = (
    <div className={`w-full ${h} rounded-full ${track}`}>
      <div
        className={`h-full rounded-full ${fill} transition-all duration-500 ease-out`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );

  if (!showLabel) return <div className={className}>{bar}</div>;

  return (
    <div
      className={`flex items-center gap-2${className ? ` ${className}` : ""}`}
    >
      {bar}
      <span className={`text-xs font-semibold whitespace-nowrap ${label}`}>
        {Math.round(percentage)}%
      </span>
    </div>
  );
};
